import { createRootRoute } from "@tanstack/react-router";
import { DecoRootLayout } from "@decocms/tanstack";
import OneDollarStats from "@decocms/apps-website/components/OneDollarStats";
import { AnalyticsRegistrar } from "~/components/Analytics";
import NavigationProgressBar from "~/components/ui/NavigationProgressBar";
import Sourei, {
  DECO_EVENTS_BRIDGE_SCRIPT,
  SESSION_FETCH_SHIM_SCRIPT,
  SOUREI_COOKIE_SHIELD_SCRIPT,
} from "~/components/analytics/Sourei";
import { SITE } from "~/constants";
import { HTML_PREFETCH_SCRIPT } from "~/sdk/htmlPrefetch";
import {
  SOUREI_SHIELD_SCRIPT_ID,
  THIRD_PARTY_GATE_SCRIPT_ID,
  thirdPartyGateScript,
} from "~/sdk/deferThirdParty";
import {
  KONFIDENCY_CAROUSEL_FOUR_SLIDES,
  KONFIDENCY_STARS_OPEN_MODAL,
  KONFIDENCY_TOP_RATED_DEDUPE,
  KONFIDENCY_TOP_RATED_RETRY,
} from "~/sdk/konfidency";
// @ts-ignore Vite ?url import
import appCss from "../styles/app.css?url";

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: SITE.DEFAULT_TITLE },
      {
        name: "description",
        content:
          "Comprar Produtos Eróticos com Preço de Atacado é na Miess, o maior Sex Shop do Brasil com pgto 10X sem Juros e Frete Grátis SP acima de R$ 250 em compras",
      },
      { property: "og:site_name", content: SITE.NAME },
      { property: "og:locale", content: "pt_BR" },
      { property: "og:type", content: "website" },
    ],
    links: [
      // Mulish is the brand body font — referenced in app.css `--font-sans`.
      // Prod loads it via Google Fonts; cand was missing the @font-face declarations,
      // so every text node fell back to system-ui. The render delta showed up as
      // ~16% pixel diff on a single h1 (parity section #h1) — same letters, different
      // anti-aliasing because the glyph shapes differ.
      //
      // Agora auto-hospedada (`@font-face` no topo de app.css + arquivos em
      // public/fonts). O <link> para fonts.googleapis.com que morava aqui
      // custava 1.508 ms de render-blocking no mobile — 1 KB de CSS, mas
      // duas origens novas (googleapis -> gstatic) no caminho do primeiro
      // paint. Os dois `preconnect` saíram junto: não há mais o que
      // preconectar.
      //
      // Preload só do subset latin normal: é o único que toda página carrega
      // (latin-ext entra sob demanda pelo unicode-range, e o itálico é raro
      // no layout). `crossOrigin` é obrigatório mesmo same-origin — fonte é
      // CORS-mode por spec, e sem isso o browser baixa DUAS vezes.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/Mulish-latin.woff2",
        crossOrigin: "anonymous",
      },
      { rel: "stylesheet", href: appCss },
      // O PageSEO emite o mesmo ícone como `shortcut icon`, mas ~70 páginas não
      // têm PageSEO (SeoV2 sozinho, institucionais, sem SEO) e dependem deste.
      { rel: "icon", href: SITE.FAVICON },
      // O #260 portou o manifest para `components/GlobalTags.tsx`, que nada
      // importa — o <link> nunca chegou ao HTML.
      { rel: "manifest", href: "/site.webmanifest" },
    ],
    // Konfidency reviews loader. It scans the DOM for `konfidency-*` divs
    // (rendered by ProductCard/Header/Reviews/InfosAndBuyButton via data-sku)
    // and injects star ratings + reviews. The legacy GlobalTags component
    // rendered this as body JSX, which TanStack does not hoist to <head> —
    // so the script never loaded. Declaring it here routes it through
    // <HeadContent/>/<Scripts/> so it actually executes.
    scripts: [
      { children: HTML_PREFETCH_SCRIPT },
      // Two fixups the origin ships next to the loader in `GlobalTags.tsx`:
      // re-attach the Top Rated badge as lazy Deco shelves mount, and force the
      // store-review carousel to 4 slides on desktop (account default is 5).
      // Both poll/retry, so they don't depend on the deferred loader above
      // having executed. See `src/sdk/konfidency.ts`.
      // Precisa vir ANTES do loader (que está no gate lá embaixo): o shim
      // troca XMLHttpRequest.prototype.send e só vale para XHRs abertos depois.
      { children: KONFIDENCY_TOP_RATED_DEDUPE },
      { children: KONFIDENCY_TOP_RATED_RETRY },
      { children: KONFIDENCY_CAROUSEL_FOUR_SLIDES },
      // Estrelas/"Clique e veja!" da PDP abrem o modal em vez de só rolar.
      { children: KONFIDENCY_STARS_OPEN_MODAL },
      // Sourei GTM cookie shield — the CMS Site block declares it as the
      // `site/sections/Analytics/SoureiCookieShield.tsx` global, but analytics
      // sections are skipped during CMS resolution, so it's wired here. The
      // container injects the BonifiQ/MPoints floating loyalty button plus
      // Clarity/FB pixel/btg360/Mailbiz/Wake (DECO-5422); the <noscript>
      // fallback lives in <Sourei /> below.
      //
      // O loader do Konfidency sai do carregamento eager e passa pelo gate de
      // ~/sdk/deferThirdParty: primeiro gesto humano, ou o fallback ancorado no
      // `load`, ou saída da página. Ele e o container respondem juntos por
      // ~4,6 s de main thread (bootup-time medido), e o TBT de 2.780 ms custava
      // 29,1 dos 37 pontos perdidos no mobile (#232). O container VOLTOU para
      // eager em #246 — ver o bloco logo abaixo.
      //
      // Desde 14/09/2026 a origem não carrega mais `gtm.js?id=…` e sim o cookie
      // shield, que precisa ler o `_wascer` do localStorage e montar a query
      // `&bi=` ANTES de injetar o próprio `<script async>`. Por isso ele não
      // bloqueia o parser mesmo eager, o que subsume o motivo do `async` que a
      // gente tinha forçado à mão na tag antiga (#165).
      //
      // The session-fetch shim must still come first: it is inline, so the
      // parser runs it before it even reaches the gate, and container tags
      // call `https://www.miess.com.br/api/sessions` once the container
      // evaluates — off production that is a CORS-blocked cross-origin request
      // (DECO-5824).
      { children: SESSION_FETCH_SHIM_SCRIPT },
      // Ponte `window.DECO.events` → `window.dataLayer`. A origem removeu a
      // dela em 14/09/2026 junto com o loader antigo do GTM, e nada ocupou o
      // lugar: os eventos de ecommerce continuam sendo despachados pelo
      // `AnalyticsRegistrar`, mas nenhum subscriber os empurra para o
      // `dataLayer`, então o GA4 não recebe `view_item`/`add_to_cart`/`purchase`.
      // Vem ANTES do cookie shield para que o `dataLayer` já exista quando o
      // container chegar. Detalhes em `~/components/analytics/Sourei.tsx`.
      { children: DECO_EVENTS_BRIDGE_SCRIPT },
      // EXPERIMENTO (#246): o cookie shield saiu do gate e voltou a carregar
      // eager. Motivo: a pílula flutuante do MPoints/BonifiQ é uma tag DENTRO
      // deste container, então com o gate ela só aparecia no primeiro gesto
      // humano ou 15 s depois do `load` — para quem só olha a página, ela
      // "aparecia e sumia" sem padrão (medido em 15/09/2026: nenhum terceiro
      // aos 10 s, todos aos 36 s).
      //
      // O CUSTO É CONHECIDO E É O PONTO DA MEDIÇÃO: este container traz junto
      // Clarity, pixel do FB, btg360, Mailbiz e Wake, que respondem por boa
      // parte dos ~4,6 s de main thread do `bootup-time` — o gate valia 29,1
      // dos 37 pontos perdidos no mobile (#232). Rodar Lighthouse neste preview
      // e comparar com o baseline É o entregável; se o TBT voltar a doer mais
      // do que a pílula vale, reverter é mover esta linha de volta para o
      // array de `snippets` do gate logo abaixo.
      //
      // O Konfidency FICA no gate: ele não tem UI flutuante e é o maior ofensor
      // isolado (1.443 ms), então mantê-lo adiado isola a variável.
      {
        id: SOUREI_SHIELD_SCRIPT_ID,
        children: SOUREI_COOKIE_SHIELD_SCRIPT,
      },
      {
        id: THIRD_PARTY_GATE_SCRIPT_ID,
        children: thirdPartyGateScript([SITE.KONFIDENCY_SCRIPT]),
      },
    ],
  }),
  component: RootLayout,
});

function RootLayout() {
  return (
    <DecoRootLayout
      lang="pt-BR"
      siteName="miess"
      // Default DaisyUI `bg-base-200` paints every page gray. Original
      // miess.com.br renders <body> with no class (white). Match that.
      bodyClassName="text-base-content"
    >
      <NavigationProgressBar />
      <AnalyticsRegistrar />
      <Sourei />
      <OneDollarStats />
    </DecoRootLayout>
  );
}
